import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Copy, ExternalLink, RefreshCw, Watch, ShoppingBag, Mail, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { COLOR_HEX, COLOR_LABELS } from "@/data/watchData";
import { useApp } from "@/context/AppContext";
import { LOCAL_ADMIN_KEY, loadLocalOptions, saveLocalOptions, loadLocalPayments, saveLocalPayments } from "@/data/localAdmin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORAGE = "sommet_admin_key";

const PAY_TYPES = {
  paypal: { label: "PayPal", detailLabel: "PayPal E-Mail", placeholder: "zahlung@deine-firma.ch" },
  twint:  { label: "TWINT", detailLabel: "TWINT Nummer", placeholder: "076 000 00 00" },
  bank:   { label: "Banküberweisung", detailLabel: "IBAN / Kontoinhaber", placeholder: "CH00 0000 0000 0000 0000 0 · Name" },
  card:   { label: "Kreditkarte", detailLabel: "Hinweis (optional)", placeholder: "Zahlungslink folgt per E-Mail" },
  other:  { label: "Andere", detailLabel: "Zahlungsdetails", placeholder: "z. B. Barzahlung bei Abholung" },
};

const CATEGORY_LABEL = { case: "Gehäuse", dial: "Zifferblatt", hands: "Zeiger", strap: "Armband" };

const AdminSaved = () => {
  const { lang, refreshCustomOptions, refreshPaymentMethods } = useApp();
  const navigate = useNavigate();
  const [key, setKey] = useState(() => {
    try { return localStorage.getItem(KEY_STORAGE) || ""; } catch { return ""; }
  });
  const [authed, setAuthed] = useState(false);
  const [localMode, setLocalMode] = useState(false);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customOpts, setCustomOpts] = useState([]);
  const [newOpt, setNewOpt] = useState({ category: "dial", label: "", color_hex: "#8a6538", image_data: "" });
  const [savingOpt, setSavingOpt] = useState(false);
  const [payMethods, setPayMethods] = useState([]);
  const [newPay, setNewPay] = useState({ type: "paypal", label: "PayPal", details: "" });

  const load = async (k) => {
    setLoading(true);
    try {
      const [cfg, st, custom, pay] = await Promise.all([
        axios.get(`${API}/admin/configurations`, { params: { key: k } }),
        axios.get(`${API}/admin/stats`, { params: { key: k } }),
        axios.get(`${API}/custom-options`),
        axios.get(`${API}/payment-methods`),
      ]);
      setItems(cfg.data.configurations);
      setStats(st.data);
      setCustomOpts(custom.data.options || []);
      setPayMethods(pay.data.methods || []);
      setAuthed(true);
      try { localStorage.setItem(KEY_STORAGE, k); } catch { /* ignore */ }
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error(lang === "de" ? "Falscher Admin-Schlüssel" : "Invalid admin key");
        setAuthed(false);
      } else if (k === LOCAL_ADMIN_KEY) {
        // Server not reachable (no backend / database yet) -> local mode
        setLocalMode(true);
        setItems([]);
        setStats(null);
        setCustomOpts(loadLocalOptions());
        setPayMethods(loadLocalPayments());
        setAuthed(true);
        try { localStorage.setItem(KEY_STORAGE, k); } catch { /* ignore */ }
        toast.message(lang === "de" ? "Server nicht erreichbar – Lokaler Modus" : "Server unreachable – local mode");
      } else {
        toast.error(lang === "de" ? "Falscher Admin-Schlüssel" : "Invalid admin key");
        setAuthed(false);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doLogin = (e) => {
    e.preventDefault();
    load(key);
  };

  const copyLink = (id) => {
    const url = `${window.location.origin}/configurator?share=${id}`;
    navigator.clipboard.writeText(url).catch(() => {});
    toast.success(lang === "de" ? "Link kopiert!" : "Link copied!");
  };

  const handleImageFile = (file) => {
    if (!file) return;
    // Downscale to max 1400px and compress (keeps transparency) so uploads
    // stay small enough for the database / browser storage.
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, 1400 / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        const data = canvas.toDataURL("image/webp", 0.88);
        setNewOpt((o) => ({ ...o, image_data: data }));
      };
      img.onerror = () => setNewOpt((o) => ({ ...o, image_data: reader.result }));
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const addCustomOption = async (e) => {
    e.preventDefault();
    if (!newOpt.label.trim()) { toast.error("Bitte einen Namen eingeben"); return; }
    setSavingOpt(true);
    if (localMode) {
      const opt = {
        id: `local-${Date.now()}`,
        category: newOpt.category,
        label: newOpt.label.trim(),
        color_hex: newOpt.color_hex,
        image_data: newOpt.image_data || null,
      };
      const next = [...customOpts, opt];
      if (!saveLocalOptions(next)) {
        toast.error("Speicher voll – bitte ein kleineres Bild verwenden");
      } else {
        setCustomOpts(next);
        setNewOpt({ category: newOpt.category, label: "", color_hex: "#8a6538", image_data: "" });
        refreshCustomOptions();
        toast.success("Option hinzugefügt (nur in diesem Browser)");
      }
      setSavingOpt(false);
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/custom-options`, {
        category: newOpt.category,
        label: newOpt.label.trim(),
        color_hex: newOpt.color_hex,
        image_data: newOpt.image_data || null,
      }, { params: { key } });
      setCustomOpts((list) => [...list, res.data]);
      setNewOpt({ category: newOpt.category, label: "", color_hex: "#8a6538", image_data: "" });
      refreshCustomOptions();
      toast.success("Option hinzugefügt");
    } catch {
      toast.error("Fehler beim Speichern");
    } finally {
      setSavingOpt(false);
    }
  };

  const addPayment = async (e) => {
    e.preventDefault();
    if (!newPay.label.trim()) { toast.error("Bitte einen Namen eingeben"); return; }
    const payload = { type: newPay.type, label: newPay.label.trim(), details: newPay.details.trim() };
    if (localMode) {
      const next = [...payMethods, { id: `local-${Date.now()}`, ...payload }];
      saveLocalPayments(next);
      setPayMethods(next);
    } else {
      try {
        const res = await axios.post(`${API}/admin/payment-methods`, payload, { params: { key } });
        setPayMethods((list) => [...list, res.data]);
      } catch {
        toast.error("Fehler beim Speichern");
        return;
      }
    }
    setNewPay({ type: newPay.type, label: PAY_TYPES[newPay.type].label, details: "" });
    refreshPaymentMethods();
    toast.success("Zahlungsmethode hinzugefügt");
  };

  const deletePayment = async (id) => {
    if (localMode) {
      const next = payMethods.filter((m) => m.id !== id);
      saveLocalPayments(next);
      setPayMethods(next);
    } else {
      try {
        await axios.delete(`${API}/admin/payment-methods/${id}`, { params: { key } });
        setPayMethods((list) => list.filter((m) => m.id !== id));
      } catch {
        toast.error("Fehler beim Entfernen");
        return;
      }
    }
    refreshPaymentMethods();
    toast.success("Entfernt");
  };

  const deleteCustomOption = async (id) => {
    if (localMode) {
      const next = customOpts.filter((o) => o.id !== id);
      saveLocalOptions(next);
      setCustomOpts(next);
      refreshCustomOptions();
      toast.success("Entfernt");
      return;
    }
    try {
      await axios.delete(`${API}/admin/custom-options/${id}`, { params: { key } });
      setCustomOpts((list) => list.filter((o) => o.id !== id));
      refreshCustomOptions();
      toast.success("Entfernt");
    } catch {
      toast.error("Fehler beim Entfernen");
    }
  };

  if (!authed) {
    return (
      <main data-testid="admin-login" className="pt-32 pb-24 min-h-screen bg-[#170F09]">
        <div className="max-w-md mx-auto px-6">
          <div className="kicker mb-4">SOMMET · Atelier</div>
          <h1 className="section-heading text-4xl md:text-5xl mb-8">Admin-Zugang</h1>
          <form onSubmit={doLogin} className="space-y-6 border border-white/8 p-8">
            <div>
              <label className="kicker mb-2 block">Admin-Schlüssel</label>
              <input
                data-testid="admin-key-input"
                type="password"
                required
                value={key}
                onChange={(e) => setKey(e.target.value)}
                className="w-full bg-transparent border-b border-white/20 focus:border-[#C9A36B] py-2 text-white outline-none"
              />
            </div>
            <button data-testid="admin-login-button" type="submit" disabled={loading} className="btn-champagne w-full disabled:opacity-60">
              {loading ? "..." : "Zugang"}
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="admin-page" className="pt-32 pb-24 min-h-screen bg-[#170F09]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
          <div>
            <div className="kicker mb-3">SOMMET · Atelier</div>
            <h1 className="section-heading text-4xl md:text-5xl">Kunden-Konfigurationen</h1>
            <p className="text-white/55 mt-2">Alle gespeicherten Uhren-Designs deiner Kunden.</p>
          </div>
          <button
            data-testid="admin-refresh"
            onClick={() => load(key)}
            className="btn-ghost flex items-center gap-2"
            disabled={loading}
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Aktualisieren
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            <StatCard Icon={Watch} label="Konfigurationen" value={stats.configurations} />
            <StatCard Icon={ShoppingBag} label="Bestellungen" value={stats.orders} />
            <StatCard Icon={Mail} label="Newsletter" value={stats.newsletter} />
            <StatCard Icon={Mail} label="Kontakte" value={stats.contacts} />
          </div>
        )}

        {localMode && (
          <div data-testid="admin-local-banner" className="mb-8 border border-[#C9A36B]/40 bg-[#C9A36B]/[0.06] p-4 text-sm text-white/80">
            <strong className="text-[#C9A36B]">Lokaler Modus:</strong> Der Server ist nicht erreichbar. Eigene Optionen werden nur in diesem Browser gespeichert.
            Sobald das Backend läuft (und <code>REACT_APP_BACKEND_URL</code> gesetzt ist), werden sie für alle Besucher gespeichert.
          </div>
        )}

        {/* Payment methods */}
        <div className="mb-16 border border-white/8" data-testid="admin-payments">
          <div className="p-6 md:p-8 border-b border-white/8">
            <div className="kicker mb-2">Bezahlung</div>
            <h2 className="font-heading text-2xl text-white mb-1">Zahlungsmethoden</h2>
            <p className="text-white/50 text-sm">
              PayPal-E-Mail, TWINT, IBAN usw. – erscheint im Checkout. Hier nur öffentliche Zahlungsangaben eintragen,
              <strong className="text-white/70"> keine Passwörter oder API-Schlüssel</strong>, da die Angaben Kunden angezeigt werden.
            </p>
          </div>

          <form onSubmit={addPayment} className="p-6 md:p-8 border-b border-white/8 grid md:grid-cols-[1fr_1fr_2fr_auto] gap-4 items-end">
            <div>
              <label className="kicker mb-2 block text-[0.6rem]">Art</label>
              <select
                data-testid="pay-type"
                value={newPay.type}
                onChange={(e) => setNewPay({ type: e.target.value, label: PAY_TYPES[e.target.value].label, details: "" })}
                className="w-full bg-transparent border border-white/15 py-2.5 px-3 text-white outline-none focus:border-[#C9A36B]"
              >
                {Object.entries(PAY_TYPES).map(([id, tp]) => <option key={id} value={id} className="bg-[#170F09]">{tp.label}</option>)}
              </select>
            </div>
            <div>
              <label className="kicker mb-2 block text-[0.6rem]">Anzeigename</label>
              <input
                data-testid="pay-label"
                value={newPay.label}
                onChange={(e) => setNewPay((p) => ({ ...p, label: e.target.value }))}
                className="w-full bg-transparent border border-white/15 py-2.5 px-3 text-white outline-none focus:border-[#C9A36B]"
              />
            </div>
            <div>
              <label className="kicker mb-2 block text-[0.6rem]">{PAY_TYPES[newPay.type].detailLabel}</label>
              <input
                data-testid="pay-details"
                value={newPay.details}
                maxLength={300}
                onChange={(e) => setNewPay((p) => ({ ...p, details: e.target.value }))}
                placeholder={PAY_TYPES[newPay.type].placeholder}
                className="w-full bg-transparent border border-white/15 py-2.5 px-3 text-white outline-none focus:border-[#C9A36B] placeholder:text-white/30"
              />
            </div>
            <button data-testid="pay-submit" type="submit" className="btn-champagne h-11">
              <Plus size={16} /> Hinzufügen
            </button>
          </form>

          <div className="p-6 md:p-8">
            {payMethods.length === 0 ? (
              <p className="text-white/40 text-sm">Noch keine eigenen Zahlungsmethoden – im Checkout werden die Standard-Methoden angezeigt.</p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {payMethods.map((m) => (
                  <div key={m.id} data-testid={`pay-item-${m.id}`} className="flex items-center gap-3 border border-white/8 p-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-white/85 text-sm truncate">{m.label}</div>
                      <div className="text-white/45 text-xs truncate">{m.details || "—"}</div>
                    </div>
                    <button
                      data-testid={`pay-delete-${m.id}`}
                      onClick={() => deletePayment(m.id)}
                      aria-label="Entfernen"
                      className="text-white/40 hover:text-red-400 transition-colors shrink-0"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Custom colors/images */}
        <div className="mb-16 border border-white/8">
          <div className="p-6 md:p-8 border-b border-white/8">
            <div className="kicker mb-2">Eigene Optionen</div>
            <h2 className="font-heading text-2xl text-white mb-1">Farben &amp; Bilder hinzufügen</h2>
            <p className="text-white/50 text-sm">
              Erscheint sofort als zusätzliche Auswahl im Konfigurator. Mit Foto wird das Foto als echte Ebene verwendet, ohne Foto nur als Farbkreis.
            </p>
          </div>

          <form onSubmit={addCustomOption} className="p-6 md:p-8 border-b border-white/8 grid md:grid-cols-[1fr_1fr_auto_auto_auto] gap-4 items-end">
            <div>
              <label className="kicker mb-2 block text-[0.6rem]">Kategorie</label>
              <select
                data-testid="custom-opt-category"
                value={newOpt.category}
                onChange={(e) => setNewOpt((o) => ({ ...o, category: e.target.value }))}
                className="w-full bg-transparent border border-white/15 py-2.5 px-3 text-white outline-none focus:border-[#C9A36B]"
              >
                {Object.entries(CATEGORY_LABEL).map(([id, lbl]) => <option key={id} value={id} className="bg-[#170F09]">{lbl}</option>)}
              </select>
            </div>
            <div>
              <label className="kicker mb-2 block text-[0.6rem]">Name</label>
              <input
                data-testid="custom-opt-label"
                value={newOpt.label}
                onChange={(e) => setNewOpt((o) => ({ ...o, label: e.target.value }))}
                placeholder="z.B. Türkis"
                className="w-full bg-transparent border border-white/15 py-2.5 px-3 text-white outline-none focus:border-[#C9A36B] placeholder:text-white/30"
              />
            </div>
            <div>
              <label className="kicker mb-2 block text-[0.6rem]">Farbe</label>
              <input
                data-testid="custom-opt-color"
                type="color"
                value={newOpt.color_hex}
                onChange={(e) => setNewOpt((o) => ({ ...o, color_hex: e.target.value }))}
                className="w-14 h-11 bg-transparent border border-white/15 cursor-pointer"
              />
            </div>
            <div>
              <label className="kicker mb-2 block text-[0.6rem]">Bild (optional)</label>
              <label className="flex items-center gap-2 border border-white/15 py-2.5 px-3 cursor-pointer hover:border-[#C9A36B] transition-colors text-sm text-white/70">
                <Upload size={14} />
                {newOpt.image_data ? "Bild gewählt" : "Datei wählen"}
                <input
                  data-testid="custom-opt-image"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleImageFile(e.target.files?.[0])}
                />
              </label>
            </div>
            <button
              data-testid="custom-opt-submit"
              type="submit"
              disabled={savingOpt}
              className="btn-champagne h-11 disabled:opacity-60"
            >
              <Plus size={16} /> Hinzufügen
            </button>
          </form>

          <div className="p-6 md:p-8">
            {customOpts.length === 0 ? (
              <p className="text-white/40 text-sm">Noch keine eigenen Optionen.</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {customOpts.map((o) => (
                  <div key={o.id} data-testid={`custom-opt-${o.id}`} className="flex items-center gap-3 border border-white/8 p-3">
                    {o.image_data ? (
                      <img src={o.image_data} alt={o.label} className="w-10 h-10 object-cover rounded-full border border-white/20" />
                    ) : (
                      <span className="w-10 h-10 rounded-full border border-white/20 shrink-0" style={{ background: o.color_hex }} />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-white/85 text-sm truncate">{o.label}</div>
                      <div className="kicker text-[0.55rem]">{CATEGORY_LABEL[o.category]}</div>
                    </div>
                    <button
                      data-testid={`custom-opt-delete-${o.id}`}
                      onClick={() => deleteCustomOption(o.id)}
                      aria-label="Entfernen"
                      className="text-white/40 hover:text-red-400 transition-colors shrink-0"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* List */}
        {items.length === 0 ? (
          <div className="text-white/50 text-center py-16 border border-white/10">
            Noch keine Konfigurationen gespeichert.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((item) => {
              const cfg = item.config;
              const img = `/watches/hero-${cfg.dial}-${cfg.strap}-t.png`;
              return (
                <div
                  key={item.id}
                  data-testid={`admin-config-${item.share_id}`}
                  className="border border-white/8 bg-[#21160B] overflow-hidden"
                >
                  <div className="aspect-square bg-gradient-to-br from-[#2C1E10] to-[#170F09] flex items-center justify-center p-4">
                    <img src={img} alt="watch" className="max-w-full max-h-full object-contain" onError={(e) => e.target.style.display='none'} />
                  </div>
                  <div className="p-5 space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="kicker">#{item.share_id}</div>
                        <div className="font-heading text-xl mt-1">
                          {cfg.engraving ? `«${cfg.engraving}»` : "Custom Design"}
                        </div>
                      </div>
                      <div className="font-mono text-[#C9A36B] text-lg">CHF {cfg.price?.toFixed?.(0) ?? cfg.price}</div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {[
                        ["Gehäuse", cfg.case],
                        ["Zifferblatt", cfg.dial],
                        ["Zeiger", cfg.hands],
                        ["Armband", cfg.strap],
                      ].map(([label, value]) => (
                        <div key={label} className="flex items-center gap-2">
                          <span className="w-3.5 h-3.5 rounded-full border border-white/20 shrink-0" style={{ background: COLOR_HEX[value] }} />
                          <div className="text-white/60">
                            <span className="text-white/40">{label}</span> <span className="text-white/85">{COLOR_LABELS[lang][value]}</span>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="text-xs text-white/40 font-mono">
                      {new Date(item.created_at).toLocaleString("de-CH")}
                    </div>

                    <div className="flex gap-2 pt-2 border-t border-white/8">
                      <button
                        onClick={() => copyLink(item.share_id)}
                        className="flex-1 border border-white/15 hover:border-[#C9A36B] text-xs py-2 flex items-center justify-center gap-2 transition-colors"
                        data-testid={`copy-link-${item.share_id}`}
                      >
                        <Copy size={12} /> Link
                      </button>
                      <button
                        onClick={() => navigate(`/configurator?share=${item.share_id}`)}
                        className="flex-1 border border-white/15 hover:border-[#C9A36B] text-xs py-2 flex items-center justify-center gap-2 transition-colors"
                        data-testid={`open-config-${item.share_id}`}
                      >
                        <ExternalLink size={12} /> Öffnen
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
};

const StatCard = ({ Icon, label, value }) => (
  <div className="border border-white/8 p-6">
    <Icon size={18} className="text-[#C9A36B] mb-3" strokeWidth={1.4} />
    <div className="font-heading text-3xl text-white">{value}</div>
    <div className="kicker mt-1">{label}</div>
  </div>
);

export default AdminSaved;
